import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormHelperText,
  Snackbar,
  Tooltip,
} from "@mui/material";
import {
  NotificationsActive as NotificationsActiveIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  FileDownload as FileDownloadIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Announcement as AnnouncementIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import {
  GetAllNotices,
  SetNoticeFilters,
  ClearNoticeFilters,
  NoticeFilterRequest,
  CreateNotice,
} from "./NoticeAction";

// Styles remain the same
const globalStyles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes fadeUp {
    from { opacity:0; transform:translateY(14px); }
    to   { opacity:1; transform:translateY(0); }
  }
  @keyframes orbPulse1 {
    0%,100% { transform: scale(1) translate(0,0); opacity:.45; }
    50%     { transform: scale(1.1) translate(20px,-15px); opacity:.65; }
  }
  @keyframes orbPulse2 {
    0%,100% { transform: scale(1) translate(0,0); opacity:.35; }
    50%     { transform: scale(1.07) translate(-20px,15px); opacity:.55; }
  }
  @keyframes particle {
    0%   { transform: translateY(0) translateX(0); opacity:0; }
    10%  { opacity:.8; }
    90%  { opacity:.3; }
    100% { transform: translateY(-110vh) translateX(var(--dx)); opacity:0; }
  }
  .ni-particle {
    position: fixed;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0,220,210,.8) 0%, rgba(0,160,190,.3) 60%, transparent 100%);
    animation: particle var(--dur) var(--delay) ease-in infinite;
    pointer-events: none;
    z-index: 0;
  }
  .ni-grid-bg {
    position: fixed;
    inset: 0;
    pointer-events: none;
    background-image:
      linear-gradient(rgba(0,210,200,.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(0,210,200,.025) 1px, transparent 1px);
    background-size: 55px 55px;
    z-index: 0;
  }
  .ni-shimmer-line {
    height: 2px;
    background: linear-gradient(90deg, transparent, #00d2c8, #48cae4, #9b5de5, #00d2c8, transparent);
    background-size: 200% auto;
    animation: shimmer 3s linear infinite;
  }
`;

// ── Dark navy/teal palette matching MyInformation ──
const D = {
  teal: "#00d2c8",
  tealDark: "#00a89e",
  cyan: "#48cae4",
  text: "#e8f4f6",
  textSub: "#7ab0b8",
  border: "rgba(0,210,200,.18)",
  cardBg: "linear-gradient(160deg, rgba(12,28,45,.96) 0%, rgba(7,18,32,.98) 100%)",
  filterBg: "linear-gradient(160deg, rgba(10,24,40,.97) 0%, rgba(6,16,28,.98) 100%)",
  headerBg: "linear-gradient(90deg, rgba(0,160,155,.32) 0%, rgba(80,50,160,.2) 50%, rgba(0,120,175,.22) 100%)",
};

const priorityColor = (p: string) => {
  switch (p?.toLowerCase()) {
    case "high": return { bg: "#e53935", text: "#fff" };
    case "medium": return { bg: "#f57c00", text: "#fff" };
    case "low": return { bg: "#00c853", text: "#fff" };
    case "archive": return { bg: "#7b1fa2", text: "#fff" };
    default: return { bg: "rgba(0,210,200,.15)", text: D.teal };
  }
};

const readColor = (pct: number) => {
  if (pct === 100) return D.teal;
  if (pct >= 50) return "#f57c00";
  return "#e53935";
};

const categoryBorder = (cat: string) => {
  const map: Record<string, string> = {
    Announcement: D.teal,
    General: "#e53935",
    Archive: "#7b1fa2",
    Holiday: "#00c853",
    "Policy Update": D.cyan,
    IT: "#f57c00",
    Facilities: "#f57c00",
  };
  return map[cat] ?? D.teal;
};

// NoticeItem interface matching API response
interface NoticeItem {
  id: number;
  category: string;
  priority: string;
  year: string;
  title: string;
  content: string;
  author: string;
  commentCount: number;
  readCount: number;
  totalCount: number;
  readPercentage: number;
  createdDate?: string;
  isActive?: boolean;
}

// Notice payload interface for create/edit/delete
interface NoticePayload {
  action: string; // I=Insert, U=Update, D=Delete
  id: number;
  category: string;
  priority: string;
  year: string;
  title: string;
  content: string;
  author: string;
  commentCount: number;
  readCount: number;
  totalCount: number;
  isActive: boolean;
}

interface Props {
  // Redux props
  notices: NoticeItem[];
  filters: NoticeFilterRequest;
  loading: boolean;
  error: string | null;
  lastResponse: any;
  createSuccess?: boolean;
  createError?: string | null;
  successMessage?: string | null;

  // Redux actions
  GetAllNotices: (filterRequest: NoticeFilterRequest) => void;
  SetNoticeFilters: (filters: NoticeFilterRequest) => void;
  ClearNoticeFilters: () => void;
  CreateNotice: (noticeData: NoticePayload) => Promise<any>;

  // Original props
  autoPlay?: boolean;
  autoPlayInterval?: number;
  onNoticeClose?: (id: number) => void;
  onNoticeClick?: (n: NoticeItem) => void;
  onNoticeEdit?: (n: NoticeItem) => void;
  maxNotices?: number;
  showNavigation?: boolean;
  showIndicators?: boolean;
}

interface State {
  search: string;
  yearFilter: string;
  catFilter: string;
  priFilter: string;
  // Dialog state
  isCreateDialogOpen: boolean;
  isEditDialogOpen: boolean;
  editingNotice: NoticeItem | null;
  newNotice: NoticePayload;
  formErrors: Partial<Record<keyof NoticePayload, string>>;
  isSubmitting: boolean;
  submitError: string | null;
  submitSuccess: boolean;
  // Delete dialog state
  isDeleteDialogOpen: boolean;
  noticeToDelete: NoticeItem | null;
  isDeleting: boolean;
  deleteError: string | null;
  // Snackbar state
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: 'success' | 'error' | 'info' | 'warning';
  // Dialog mode
  dialogMode: 'create' | 'edit';
  roleCode: string | null;
  isAdmin: boolean; // Added admin flag
}

const PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  id: i, size: Math.random() * 4 + 2, left: Math.random() * 100,
  dur: Math.random() * 12 + 8, delay: Math.random() * 10, dx: (Math.random() - 0.5) * 100,
}));

// Available options for dropdowns
const CATEGORY_OPTIONS = ["Announcement", "General", "Archive", "Holiday", "Policy Update", "IT", "Facilities"];
const PRIORITY_OPTIONS = ["High", "Medium", "Low", "Archive"];
const YEAR_OPTIONS = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear.toString(), (currentYear - 1).toString(), (currentYear - 2).toString()];
};

class Notice extends Component<Props, State> {
  static defaultProps = {
    autoPlay: true,
    autoPlayInterval: 5000,
    maxNotices: 20,
    showNavigation: true,
    showIndicators: true,
  };

  constructor(props: Props) {
    super(props);
    const roleCode = sessionStorage.getItem('RoleCode');
    // Define admin roles - you can customize this list
    const adminRoles = ['ADMIN', 'SuperAdmin', 'Manager', 'HR Admin', 'Notice Admin'];
    const isAdmin = roleCode ? adminRoles.includes(roleCode) : false;
    
    this.state = {
      search: "",
      yearFilter: "All",
      catFilter: "All",
      priFilter: "All",
      // Dialog state
      isCreateDialogOpen: false,
      isEditDialogOpen: false,
      editingNotice: null,
      newNotice: {
        action: "I",  // Default to Insert
        id: 0,
        title: "",
        content: "",
        category: "Announcement",
        priority: "Medium",
        year: new Date().getFullYear().toString(),
        author: "Current User",
        commentCount: 0,
        readCount: 0,
        totalCount: 0,
        isActive: true
      },
      formErrors: {},
      isSubmitting: false,
      submitError: null,
      submitSuccess: false,
      // Delete dialog state
      isDeleteDialogOpen: false,
      noticeToDelete: null,
      isDeleting: false,
      deleteError: null,
      // Snackbar state
      snackbarOpen: false,
      snackbarMessage: "",
      snackbarSeverity: "success",
      dialogMode: 'create',
      roleCode: roleCode,
      isAdmin: isAdmin
    };
  }

  componentDidMount() {
    // Initialize filters from Redux if they exist
    const { category, priority, year } = this.props.filters;
    this.setState({
      catFilter: category || "All",
      priFilter: priority || "All",
      yearFilter: year || "All",
    });
    
    // Load notices with current filters
    this.loadNotices();
  }

  componentDidUpdate(prevProps: Props) {
    // Log when notices are updated
    if (prevProps.notices !== this.props.notices) {
    }
    
    // Log when loading state changes
    if (prevProps.loading !== this.props.loading) {
    }
    
    // Log errors
    if (prevProps.error !== this.props.error && this.props.error) {
      this.showSnackbar(this.props.error, "error");
    }

    // Handle create/edit success from Redux (both use createSuccess)
    if (prevProps.createSuccess !== this.props.createSuccess && this.props.createSuccess) {
      const { dialogMode } = this.state;
      this.setState({ 
        isCreateDialogOpen: false,
        isEditDialogOpen: false,
        isSubmitting: false,
        editingNotice: null
      });
      
      const successMsg = dialogMode === 'edit' 
        ? "Notice updated successfully!" 
        : "Notice created successfully!";
      
      this.showSnackbar(this.props.successMessage || successMsg, "success");
      this.loadNotices(); // Refresh the list
    }

    // Handle create/edit error from Redux
    if (prevProps.createError !== this.props.createError && this.props.createError) {
      this.setState({ 
        isSubmitting: false,
        submitError: this.props.createError 
      });
      this.showSnackbar(this.props.createError, "error");
    }
  }

  loadNotices = () => {
    // Use current filters from Redux state
    this.props.GetAllNotices(this.props.filters);
  };

  // Snackbar methods
  showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity,
    });
  };

  handleCloseSnackbar = () => {
    this.setState({ snackbarOpen: false });
  };

  // Delete confirmation handlers - only accessible to admin
  handleOpenDeleteDialog = (notice: NoticeItem, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    const { isAdmin } = this.state;
    
    if (!isAdmin) {
      this.showSnackbar("You don't have permission to delete notices", "error");
      return;
    }
    
    this.setState({
      isDeleteDialogOpen: true,
      noticeToDelete: notice,
      deleteError: null,
      isDeleting: false
    });
  };

  handleCloseDeleteDialog = () => {
    this.setState({
      isDeleteDialogOpen: false,
      noticeToDelete: null,
      deleteError: null,
      isDeleting: false
    });
  };

  handleConfirmDelete = async () => {
    const { noticeToDelete, isAdmin } = this.state;
    
    if (!isAdmin) {
      this.showSnackbar("You don't have permission to delete notices", "error");
      return;
    }
    
    if (!noticeToDelete) {
      return;
    }

    this.setState({ isDeleting: true, deleteError: null });

    try {
      // Create delete payload with action "D"
      const deletePayload: NoticePayload = {
        action: "D",  // Delete action
        id: noticeToDelete.id,
        category: noticeToDelete.category,
        priority: noticeToDelete.priority,
        year: noticeToDelete.year,
        title: noticeToDelete.title,
        content: noticeToDelete.content,
        author: noticeToDelete.author,
        commentCount: noticeToDelete.commentCount || 0,
        readCount: noticeToDelete.readCount || 0,
        totalCount: noticeToDelete.totalCount || 0,
        isActive: false
      };
      const result = await this.props.CreateNotice(deletePayload);
      
      
      if (result && result.success) {
        this.setState({ 
          isDeleteDialogOpen: false,
          noticeToDelete: null,
          isDeleting: false 
        });
        
        this.showSnackbar("Notice deleted successfully!", "success");
        this.loadNotices(); // Refresh the list
      } else {
        this.setState({ 
          isDeleting: false,
          deleteError: result?.error || "Failed to delete notice"
        });
      }
    } catch (error) {
      console.error("Error deleting notice:", error);
      this.setState({
        deleteError: error instanceof Error ? error.message : "Failed to delete notice. Please try again.",
        isDeleting: false,
      });
    }
  };

  // Dialog handlers - only accessible to admin
  handleOpenCreateDialog = () => {
    const { isAdmin } = this.state;
    
    if (!isAdmin) {
      this.showSnackbar("You don't have permission to create notices", "error");
      return;
    }
    
    // Get author from somewhere (e.g., from Redux auth state)
    const author = "Current User"; // Replace with actual user from auth
    
    this.setState({
      dialogMode: 'create',
      isCreateDialogOpen: true,
      isEditDialogOpen: false,
      editingNotice: null,
      newNotice: {
        action: "I",  // Insert action
        id: 0,
        title: "",
        content: "",
        category: "Announcement",
        priority: "Medium",
        year: new Date().getFullYear().toString(),
        author: author,
        commentCount: 0,
        readCount: 0,
        totalCount: 0,
        isActive: true
      },
      formErrors: {},
      submitError: null,
      submitSuccess: false,
    });
  };

  handleOpenEditDialog = (notice: NoticeItem) => {
    const { isAdmin } = this.state;
    
    if (!isAdmin) {
      this.showSnackbar("You don't have permission to edit notices", "error");
      return;
    }
    
    this.setState({
      dialogMode: 'edit',
      isCreateDialogOpen: true,
      isEditDialogOpen: true,
      editingNotice: notice,
      newNotice: {
        action: "U",  // Update action
        id: notice.id,
        title: notice.title,
        content: notice.content,
        category: notice.category,
        priority: notice.priority,
        year: notice.year,
        author: notice.author,
        commentCount: notice.commentCount || 0,
        readCount: notice.readCount || 0,
        totalCount: notice.totalCount || 0,
        isActive: notice.isActive !== undefined ? notice.isActive : true
      },
      formErrors: {},
      submitError: null,
      submitSuccess: false,
    });
  };

  handleCloseCreateDialog = () => {
    this.setState({ 
      isCreateDialogOpen: false,
      isEditDialogOpen: false,
      editingNotice: null
    });
  };

  handleInputChange = (field: keyof NoticePayload) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    const value = event.target.value;
    this.setState(prev => ({
      newNotice: {
        ...prev.newNotice,
        [field]: value,
      },
      // Clear error for this field when user starts typing
      formErrors: {
        ...prev.formErrors,
        [field]: undefined,
      },
    }));
  };

  validateForm = (): boolean => {
    const errors: Partial<Record<keyof NoticePayload, string>> = {};
    const { newNotice } = this.state;

    if (!newNotice.title.trim()) {
      errors.title = "Title is required";
    } else if (newNotice.title.length < 3) {
      errors.title = "Title must be at least 3 characters";
    } else if (newNotice.title.length > 100) {
      errors.title = "Title must be less than 100 characters";
    }

    if (!newNotice.content.trim()) {
      errors.content = "Content is required";
    } else if (newNotice.content.length < 10) {
      errors.content = "Content must be at least 10 characters";
    } else if (newNotice.content.length > 5000) {
      errors.content = "Content must be less than 5000 characters";
    }

    if (!newNotice.category) {
      errors.category = "Category is required";
    }

    if (!newNotice.priority) {
      errors.priority = "Priority is required";
    }

    if (!newNotice.year) {
      errors.year = "Year is required";
    }

    if (!newNotice.author.trim()) {
      errors.author = "Author is required";
    }

    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  };

  handleSubmitNotice = async () => {
    const { isAdmin } = this.state;
    
    if (!isAdmin) {
      this.showSnackbar("You don't have permission to modify notices", "error");
      return;
    }
    
    if (!this.validateForm()) {
      return;
    }

    this.setState({ isSubmitting: true, submitError: null });

    try {
      const { dialogMode, newNotice, editingNotice } = this.state;
      
      let payload: NoticePayload;
      
      if (dialogMode === 'edit' && editingNotice) {
        // Edit mode - use action "U" and existing ID
        payload = {
          action: "U",
          id: editingNotice.id,
          category: newNotice.category,
          priority: newNotice.priority,
          year: newNotice.year,
          title: newNotice.title,
          content: newNotice.content,
          author: newNotice.author,
          commentCount: newNotice.commentCount || 0,
          readCount: newNotice.readCount || 0,
          totalCount: newNotice.totalCount || 0,
          isActive: newNotice.isActive
        };
      } else {
        payload = {
          action: "I",
          id: 0,
          category: newNotice.category,
          priority: newNotice.priority,
          year: newNotice.year,
          title: newNotice.title,
          content: newNotice.content,
          author: newNotice.author,
          commentCount: newNotice.commentCount || 0,
          readCount: newNotice.readCount || 0,
          totalCount: newNotice.totalCount || 0,
          isActive: newNotice.isActive
        };
      }
      
      // Same API call for both create and edit
      const result = await this.props.CreateNotice(payload);
      
      if (result && result.success) {
        this.setState({ 
          submitSuccess: true,
          isSubmitting: false 
        });
        // Dialog will be closed in componentDidUpdate when success becomes true
      } else {
        this.setState({ 
          isSubmitting: false,
          submitError: result?.error || `Failed to ${dialogMode === 'edit' ? 'update' : 'create'} notice`
        });
      }
    } catch (error) {
      console.error("Error submitting notice:", error);
      this.setState({
        submitError: error instanceof Error ? error.message : `Failed to ${this.state.dialogMode === 'edit' ? 'update' : 'create'} notice. Please try again.`,
        isSubmitting: false,
      });
    }
  };

  handleClose = (id: number) => {
    // For local filtering (UI only)
    this.props.onNoticeClose?.(id);
  };

  handleEditClick = (notice: NoticeItem, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    this.handleOpenEditDialog(notice);
    this.props.onNoticeEdit?.(notice);
  };

  handleCategoryChange = (event: any) => {
    const category = event.target.value;
    this.setState({ catFilter: category });

    // Create new filters
    const newFilters: NoticeFilterRequest = {
      ...this.props.filters,
      category: category !== "All" ? category : undefined
    };
    
    // Update Redux filters
    this.props.SetNoticeFilters(newFilters);
    
    // MANUALLY CALL API with new filters
    this.props.GetAllNotices(newFilters);
  };

  handleYearChange = (event: any) => {
    const year = event.target.value;
    this.setState({ yearFilter: year });

    const newFilters: NoticeFilterRequest = {
      ...this.props.filters,
      year: year !== "All" ? year : undefined
    };
    
    // Update Redux filters
    this.props.SetNoticeFilters(newFilters);
    
    // MANUALLY CALL API with new filters
    this.props.GetAllNotices(newFilters);
  };

  handlePriorityChange = (event: any) => {
    const priority = event.target.value;
    this.setState({ priFilter: priority });

    const newFilters: NoticeFilterRequest = {
      ...this.props.filters,
      priority: priority !== "All" ? priority : undefined
    };
    
    // Update Redux filters
    this.props.SetNoticeFilters(newFilters);
    
    // MANUALLY CALL API with new filters
    this.props.GetAllNotices(newFilters);
  };

  handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ search: event.target.value });
    // Search is handled client-side
  };

  clearFilters = () => {
    this.setState({
      search: "",
      yearFilter: "All",
      catFilter: "All",
      priFilter: "All"
    });
    
    // Clear Redux filters
    this.props.ClearNoticeFilters();
    
    // MANUALLY CALL API with empty filters
    this.props.GetAllNotices({ includeInactive: false });
  };

  handleRefresh = () => {
    this.loadNotices();
    this.showSnackbar("Refreshing notices...", "info");
  };

  get filtered() {
    const { search, yearFilter, catFilter, priFilter } = this.state;

    // Use API notices from Redux props
    const sourceNotices = this.props.notices && Array.isArray(this.props.notices) && this.props.notices.length > 0
      ? this.props.notices
      : [];

    return sourceNotices.filter(n => {
      const s = search.toLowerCase();
      return (
        (!s || (n.title?.toLowerCase() || '').includes(s) || (n.content?.toLowerCase() || '').includes(s)) &&
        (yearFilter === "All" || n.year === yearFilter) &&
        (catFilter === "All" || n.category === catFilter) &&
        (priFilter === "All" || n.priority === priFilter)
      );
    });
  }

  getUniqueValues() {
    const sourceNotices = this.props.notices && Array.isArray(this.props.notices) && this.props.notices.length > 0
      ? this.props.notices
      : [];

    const years = ["All", ...Array.from(new Set(sourceNotices.map(n => n.year).filter(Boolean)))];
    const categories = ["All", ...Array.from(new Set(sourceNotices.map(n => n.category).filter(Boolean)))];
    const priorities = ["All", ...Array.from(new Set(sourceNotices.map(n => n.priority).filter(Boolean)))];

    return {
      years,
      categories,
      priorities
    };
  }

  renderCard(notice: NoticeItem) {
    const bord = categoryBorder(notice.category);
    const rc = readColor(notice.readPercentage);
    const priorityColors = priorityColor(notice.priority);
    const { isAdmin } = this.state;

    return (
      <Box
        key={notice.id}
        onClick={() => this.props.onNoticeClick?.(notice)}
        sx={{
          background: D.cardBg,
          borderRadius: "14px",
          border: `1px solid ${D.border}`,
          borderLeft: `4px solid ${bord}`,
          boxShadow: "0 4px 24px rgba(0,0,0,.5), 0 0 40px rgba(0,150,160,.05), inset 0 1px 0 rgba(0,210,200,.07)",
          backdropFilter: "blur(12px)",
          p: 2.5,
          cursor: "pointer",
          position: "relative",
          transition: "all .25s ease",
          animation: "fadeUp .4s ease both",
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: "radial-gradient(ellipse at 15% 20%, rgba(0,200,190,.05) 0%, transparent 55%), radial-gradient(ellipse at 85% 80%, rgba(72,100,230,.06) 0%, transparent 55%)",
            pointerEvents: "none",
            zIndex: 0,
          },
          "& > *": { position: "relative", zIndex: 1 },
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 8px 32px rgba(0,0,0,.6), 0 0 40px rgba(0,210,200,.12), inset 0 1px 0 rgba(0,210,200,.12)",
            borderColor: "rgba(0,210,200,.35)",
          },
        }}
      >
        {/* Shimmer top line */}
        <Box sx={{
          position: "absolute", top: 0, left: 0, right: 0, height: "2px",
          background: `linear-gradient(90deg, transparent, ${bord}, ${D.teal}, transparent)`,
          backgroundSize: "200% auto",
          animation: "shimmer 3s linear infinite",
        }} />

        {/* Row 1: category + priority + year */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
          <Chip
            icon={<AnnouncementIcon sx={{ fontSize: "0.75rem !important", color: `${D.teal} !important` }} />}
            label={notice.category}
            size="small"
            sx={{
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", fontWeight: 600,
              background: "rgba(0,210,200,.12)", color: D.teal,
              border: "1px solid rgba(0,210,200,.25)", height: 24,
              "& .MuiChip-icon": { ml: 0.5 },
            }}
          />
          <Chip
            label={notice.priority}
            size="small"
            sx={{
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", fontWeight: 600,
              background: priorityColors.bg, color: priorityColors.text,
              height: 24, opacity: 0.9,
            }}
          />
          <Typography sx={{
            ml: "auto", fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem",
            color: D.textSub, fontWeight: 500,
            background: "rgba(0,210,200,.08)", padding: "2px 8px", borderRadius: "12px",
            border: "1px solid rgba(0,210,200,.12)",
          }}>
            {notice.year}
          </Typography>
        </Box>

        {/* Title */}
        <Typography sx={{
          fontFamily: "'DM Sans',sans-serif", fontWeight: 600, fontSize: "0.95rem",
          color: D.text, lineHeight: 1.4,
          display: "-webkit-box", WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical", overflow: "hidden",
          textShadow: "0 0 20px rgba(0,210,200,.1)",
        }}>
          {notice.title}
        </Typography>

        {/* Content */}
        {notice.content ? (
          <Typography sx={{
            fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
            color: D.textSub, lineHeight: 1.55,
            display: "-webkit-box", WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical", overflow: "hidden",
          }}>
            {notice.content}
          </Typography>
        ) : null}

        {/* Author row */}
        <Box sx={{
          display: "flex", alignItems: "center", gap: 0.8, mt: "auto",
          background: "rgba(0,210,200,.05)", padding: "6px 10px", borderRadius: "8px",
          border: "1px solid rgba(0,210,200,.1)",
        }}>
          <PersonIcon sx={{ fontSize: "0.95rem", color: D.textSub }} />
          <Typography sx={{
            fontFamily: "'DM Sans',sans-serif", fontSize: "0.75rem", color: D.textSub,
            flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500,
          }}>
            {notice.author || "—"}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
            <VisibilityIcon sx={{ fontSize: "0.85rem", color: D.textSub }} />
            <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", color: D.teal, fontWeight: 600 }}>
              {notice.commentCount}
            </Typography>
          </Box>
        </Box>

        {/* Progress bar */}
        <Box sx={{ background: "rgba(0,210,200,.05)", padding: "8px 10px", borderRadius: "8px", border: "1px solid rgba(0,210,200,.1)" }}>
          <LinearProgress
            variant="determinate"
            value={notice.readPercentage}
            sx={{
              height: 6, borderRadius: 3,
              background: "rgba(0,210,200,.1)",
              "& .MuiLinearProgress-bar": { background: rc, borderRadius: 3, boxShadow: `0 0 6px ${rc}88` },
            }}
          />
          <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.7rem", color: D.textSub, mt: 0.5, fontWeight: 500 }}>
            Read: {notice.readCount}/{notice.totalCount} ({notice.readPercentage}%)
          </Typography>
        </Box>

        {/* Divider */}
        <Box sx={{ height: "1px", background: "linear-gradient(90deg,transparent,rgba(0,210,200,.25),transparent)", my: 0.5 }} />

        {/* Action row - Conditionally show edit/delete based on admin role */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            size="small"
            startIcon={<VisibilityIcon sx={{ fontSize: "0.8rem !important" }} />}
            sx={{
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", fontWeight: 600,
              textTransform: "none", borderRadius: "8px",
              background: "rgba(0,210,200,.1)", color: D.teal,
              border: "1px solid rgba(0,210,200,.25)", px: 1.5, py: 0.4, minWidth: "65px",
              "&:hover": { background: "rgba(0,210,200,.2)", borderColor: "rgba(0,210,200,.45)", boxShadow: "0 0 10px rgba(0,210,200,.15)" },
              "& .MuiButton-startIcon": { marginRight: "4px" },
            }}
          >
            Status
          </Button>

          <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
            {/* Edit button - only visible to admin */}
            {isAdmin && (
              <Tooltip title="Edit Notice">
                <IconButton 
                  size="small" 
                  onClick={(e) => this.handleEditClick(notice, e)}
                  sx={{
                    color: D.textSub, background: "rgba(0,210,200,.05)",
                    "&:hover": { color: D.teal, background: "rgba(0,210,200,.15)" }, padding: "6px",
                  }}
                >
                  <EditIcon sx={{ fontSize: "0.95rem" }} />
                </IconButton>
              </Tooltip>
            )}
            
            {/* Read status indicator - visible to all */}
            {notice.readPercentage === 100 ? (
              <Tooltip title="Fully Read">
                <IconButton size="small" sx={{
                  color: "#00c853", background: "rgba(0,200,83,.08)",
                  "&:hover": { background: "rgba(0,200,83,.18)" }, padding: "6px",
                }}>
                  <CheckCircleIcon sx={{ fontSize: "0.95rem" }} />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Read Progress">
                <IconButton size="small" sx={{
                  color: D.textSub, background: "rgba(0,210,200,.05)",
                  "&:hover": { color: "#f57c00", background: "rgba(245,124,0,.12)" }, padding: "6px",
                }}>
                  <CloseIcon sx={{ fontSize: "0.95rem" }} />
                </IconButton>
              </Tooltip>
            )}
            
            {/* Delete button - only visible to admin */}
            {isAdmin && (
              <Tooltip title="Delete Notice">
                <IconButton
                  size="small"
                  onClick={e => { e.stopPropagation(); this.handleOpenDeleteDialog(notice, e); }}
                  sx={{
                    color: "#ef5350", background: "rgba(229,57,53,.08)",
                    "&:hover": { color: "#ff5252", background: "rgba(229,57,53,.18)" }, padding: "6px",
                  }}
                >
                  <CloseIcon sx={{ fontSize: "0.95rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Non-admin info badge */}
        {!isAdmin && (
          <Box sx={{
            position: "absolute",
            bottom: 8,
            right: 8,
            background: "rgba(0,0,0,.5)",
            borderRadius: "4px",
            padding: "2px 6px",
            fontSize: "0.65rem",
            color: D.textSub,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backdropFilter: "blur(4px)",
          }}>
            <LockIcon sx={{ fontSize: "0.65rem" }} />
            <span>Read Only</span>
          </Box>
        )}
      </Box>
    );
  }

  renderDeleteDialog() {
    const { isDeleteDialogOpen, noticeToDelete, isDeleting, deleteError } = this.state;

    return (
      <Dialog
        open={isDeleteDialogOpen}
        onClose={this.handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: D.cardBg,
            borderRadius: "14px",
            border: `1px solid ${D.border}`,
            boxShadow: "0 8px 32px rgba(0,0,0,.6)",
            backdropFilter: "blur(12px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${D.border}`,
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 1.5
        }}>
          <WarningIcon sx={{ color: "#ef5350", fontSize: "1.8rem" }} />
          <Typography sx={{ 
            fontFamily: "'DM Sans',sans-serif", 
            fontWeight: 600, 
            fontSize: "1.2rem",
            color: D.text
          }}>
            Confirm Delete
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {noticeToDelete && (
            <>
              <Typography sx={{ 
                fontFamily: "'DM Sans',sans-serif", 
                color: D.text,
                mb: 2
              }}>
                Are you sure you want to delete this notice?
              </Typography>
              
              <Box sx={{
                background: "rgba(0,0,0,.3)",
                borderRadius: "8px",
                p: 2,
                border: `1px solid ${D.border}`,
                mb: 2
              }}>
                <Typography sx={{ 
                  fontFamily: "'DM Sans',sans-serif", 
                  color: D.teal,
                  fontWeight: 600,
                  fontSize: "1rem",
                  mb: 1
                }}>
                  {noticeToDelete.title}
                </Typography>
                <Typography sx={{ 
                  fontFamily: "'DM Sans',sans-serif", 
                  color: D.textSub,
                  fontSize: "0.9rem",
                  mb: 1
                }}>
                  {noticeToDelete.content?.substring(0, 100)}
                  {noticeToDelete.content?.length > 100 ? "..." : ""}
                </Typography>
                <Box sx={{ display: "flex", gap: 2, fontSize: "0.85rem", color: D.textSub }}>
                  <span>Category: {noticeToDelete.category}</span>
                  <span>Priority: {noticeToDelete.priority}</span>
                  <span>Year: {noticeToDelete.year}</span>
                </Box>
              </Box>

              <Typography sx={{ 
                fontFamily: "'DM Sans',sans-serif", 
                color: "#ef5350",
                fontSize: "0.9rem"
              }}>
                This action cannot be undone.
              </Typography>

              {deleteError && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: "8px" }}>
                  {deleteError}
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          borderTop: `1px solid ${D.border}`,
          p: 3,
          gap: 1
        }}>
          <Button
            onClick={this.handleCloseDeleteDialog}
            disabled={isDeleting}
            sx={{
              fontFamily: "'DM Sans',sans-serif",
              color: D.textSub,
              border: `1px solid ${D.border}`,
              '&:hover': { background: 'rgba(0,210,200,.1)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={this.handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
            sx={{
              fontFamily: "'DM Sans',sans-serif",
              background: "#ef5350",
              color: "#ffffff",
              '&:hover': { 
                background: "#ff5252",
                transform: "translateY(-1px)"
              },
              transition: "all .25s ease",
              minWidth: "100px"
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderCreateDialog() {
    const { isCreateDialogOpen, newNotice, formErrors, isSubmitting, submitSuccess, dialogMode, editingNotice } = this.state;
    const isEditMode = dialogMode === 'edit';

    // Dark theme styles for form inputs
    const inputSx = {
      "& .MuiOutlinedInput-root": {
        color: D.text,
        fontFamily: "'DM Sans',sans-serif",
        "& fieldset": { borderColor: "rgba(0,210,200,.2)" },
        "&:hover fieldset": { borderColor: "rgba(0,210,200,.45)" },
        "&.Mui-focused fieldset": { borderColor: D.teal },
      },
      "& .MuiInputLabel-root": {
        color: D.textSub,
        fontFamily: "'DM Sans',sans-serif",
        "&.Mui-focused": { color: D.teal },
      },
    };

    const selectSx = {
      color: D.text,
      fontFamily: "'DM Sans',sans-serif",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,210,200,.2)" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,210,200,.45)" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.teal },
      "& .MuiSelect-icon": { color: D.textSub },
    };

    return (
      <Dialog
        open={isCreateDialogOpen}
        onClose={this.handleCloseCreateDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: D.cardBg,
            borderRadius: "14px",
            border: `1px solid ${D.border}`,
            boxShadow: "0 8px 32px rgba(0,0,0,.6)",
            backdropFilter: "blur(12px)",
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: `1px solid ${D.border}`,
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between"
        }}>
          <Typography sx={{ 
            fontFamily: "'DM Sans',sans-serif", 
            fontWeight: 600, 
            fontSize: "1.2rem",
            color: D.text
          }}>
            {isEditMode ? 'Edit Notice' : 'Create New Notice'}
            {isEditMode && editingNotice && (
              <Typography component="span" sx={{ ml: 1, fontSize: "0.9rem", color: D.teal }}>
                (ID: {editingNotice.id})
              </Typography>
            )}
          </Typography>
          <IconButton 
            onClick={this.handleCloseCreateDialog}
            sx={{ color: D.textSub, '&:hover': { color: D.teal } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            {/* Hidden fields for action and id - for debugging */}
            <Grid size={{ xs: 12 }} sx={{ display: 'none' }}>
              <input type="hidden" value={newNotice.action} />
              <input type="hidden" value={newNotice.id} />
            </Grid>

            {/* Title - Full width */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Title"
                value={newNotice.title}
                onChange={this.handleInputChange('title')}
                error={!!formErrors.title}
                helperText={formErrors.title}
                disabled={isSubmitting || submitSuccess}
                sx={inputSx}
              />
            </Grid>

            {/* Category - 4 columns on medium screens and up */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth error={!!formErrors.category}>
                <InputLabel sx={{ color: D.textSub }}>Category</InputLabel>
                <Select
                  value={newNotice.category}
                  label="Category"
                  onChange={this.handleInputChange('category') as any}
                  disabled={isSubmitting || submitSuccess}
                  sx={selectSx}
                >
                  {CATEGORY_OPTIONS.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
                {formErrors.category && (
                  <FormHelperText sx={{ color: "#f44336" }}>{formErrors.category}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Priority - 4 columns on medium screens and up */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth error={!!formErrors.priority}>
                <InputLabel sx={{ color: D.textSub }}>Priority</InputLabel>
                <Select
                  value={newNotice.priority}
                  label="Priority"
                  onChange={this.handleInputChange('priority') as any}
                  disabled={isSubmitting || submitSuccess}
                  sx={selectSx}
                >
                  {PRIORITY_OPTIONS.map(pri => (
                    <MenuItem key={pri} value={pri}>{pri}</MenuItem>
                  ))}
                </Select>
                {formErrors.priority && (
                  <FormHelperText sx={{ color: "#f44336" }}>{formErrors.priority}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Year - 4 columns on medium screens and up */}
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth error={!!formErrors.year}>
                <InputLabel sx={{ color: D.textSub }}>Year</InputLabel>
                <Select
                  value={newNotice.year}
                  label="Year"
                  onChange={this.handleInputChange('year') as any}
                  disabled={isSubmitting || submitSuccess}
                  sx={selectSx}
                >
                  {YEAR_OPTIONS().map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
                {formErrors.year && (
                  <FormHelperText sx={{ color: "#f44336" }}>{formErrors.year}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Content - Full width */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Content"
                value={newNotice.content}
                onChange={this.handleInputChange('content')}
                error={!!formErrors.content}
                helperText={formErrors.content}
                disabled={isSubmitting || submitSuccess}
                sx={inputSx}
              />
            </Grid>

            {/* Author - Full width */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Author"
                value={newNotice.author}
                onChange={this.handleInputChange('author')}
                error={!!formErrors.author}
                helperText={formErrors.author}
                disabled={isSubmitting || submitSuccess}
                sx={inputSx}
              />
            </Grid>

            {/* Success message */}
            {submitSuccess && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="success" sx={{ borderRadius: "8px" }}>
                  Notice {isEditMode ? 'updated' : 'created'} successfully! Refreshing list...
                </Alert>
              </Grid>
            )}

            {/* Error message */}
            {this.state.submitError && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error" sx={{ borderRadius: "8px" }}>
                  {this.state.submitError}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ 
          borderTop: `1px solid ${D.border}`,
          p: 3,
          gap: 1
        }}>
          <Button
            onClick={this.handleCloseCreateDialog}
            disabled={isSubmitting}
            sx={{
              fontFamily: "'DM Sans',sans-serif",
              color: D.textSub,
              border: `1px solid ${D.border}`,
              '&:hover': { background: 'rgba(0,210,200,.1)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={this.handleSubmitNotice}
            disabled={isSubmitting || submitSuccess}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            sx={{
              fontFamily: "'DM Sans',sans-serif",
              background: `linear-gradient(90deg, ${D.tealDark}, ${D.teal})`,
              color: "#ffffff",
              '&:hover': { 
                background: `linear-gradient(90deg, ${D.teal}, ${D.tealDark})`,
                transform: "translateY(-1px)"
              },
              transition: "all .25s ease",
              minWidth: "100px"
            }}
          >
            {isSubmitting 
              ? (isEditMode ? 'Updating...' : 'Creating...') 
              : submitSuccess 
                ? (isEditMode ? 'Updated!' : 'Created!') 
                : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  render() {
    const { search, yearFilter, catFilter, priFilter, snackbarOpen, snackbarMessage, snackbarSeverity, isAdmin } = this.state;
    const { loading, error, notices } = this.props;
    const filtered = this.filtered;
    const { years, categories, priorities } = this.getUniqueValues();

    // Dark-theme select styles
    const selectSx = {
      fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem",
      color: D.text, borderRadius: "10px", height: 40,
      background: "rgba(0,210,200,.06)",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,210,200,.2)" },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,210,200,.45)" },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: D.teal },
      "& .MuiSelect-icon": { color: D.textSub },
    };

    const menuPaperSx = {
      background: "rgba(9,30,46,.98)",
      border: "1px solid rgba(0,210,200,.2)",
      borderRadius: "10px",
      boxShadow: "0 8px 32px rgba(0,0,0,.6)",
      backdropFilter: "blur(12px)",
      "& .MuiMenuItem-root": {
        fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: D.textSub,
        "&:hover": { background: "rgba(0,210,200,.1)", color: D.text },
        "&.Mui-selected": { background: "rgba(0,210,200,.15)", color: D.teal },
      },
    };

    const labelSx = {
      fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem",
      color: D.textSub, "&.Mui-focused": { color: D.teal },
    };

    return (
      <>
        <style>{globalStyles}</style>

        <Box sx={{
          minHeight: "100vh",
          background: "linear-gradient(145deg,#04111a 0%,#091e2e 35%,#0d2840 60%,#06141e 100%)",
          position: "relative", overflow: "hidden",
          fontFamily: "'DM Sans',sans-serif", color: D.text,
        }}>
          {/* Orbs */}
          <Box sx={{
            position: "fixed", top: "5%", left: "8%", width: 420, height: 420,
            borderRadius: "50%", pointerEvents: "none",
            background: "radial-gradient(circle, rgba(0,160,155,.2) 0%, transparent 68%)",
            animation: "orbPulse1 11s ease-in-out infinite", zIndex: 0,
          }} />
          <Box sx={{
            position: "fixed", bottom: "8%", right: "6%", width: 500, height: 500,
            borderRadius: "50%", pointerEvents: "none",
            background: "radial-gradient(circle, rgba(80,50,200,.14) 0%, transparent 68%)",
            animation: "orbPulse2 15s ease-in-out infinite", zIndex: 0,
          }} />

          {/* Grid */}
          <div className="ni-grid-bg" />

          {/* Particles */}
          {PARTICLES.map(p => (
            <div key={p.id} className="ni-particle" style={{
              width: p.size, height: p.size, left: `${p.left}%`, bottom: -p.size,
              ["--dur" as any]: `${p.dur}s`, ["--delay" as any]: `${p.delay}s`, ["--dx" as any]: `${p.dx}px`,
            }} />
          ))}

          <Box sx={{ position: "relative", zIndex: 1, p: { xs: 2, sm: 3, md: 4 } }}>

            {/* ── Header ── */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3, flexWrap: "wrap" }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: "14px", flexShrink: 0,
                background: "linear-gradient(135deg, rgba(0,210,200,.15) 0%, rgba(0,140,200,.1) 100%)",
                border: "1px solid rgba(0,210,200,.25)",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: "0 4px 16px rgba(0,210,200,.15)",
              }}>
                <NotificationsActiveIcon sx={{ fontSize: "1.5rem", color: D.teal, filter: "drop-shadow(0 0 6px rgba(0,210,200,.5))" }} />
              </Box>
              <Box>
                <Typography sx={{
                  fontFamily: "'DM Sans',sans-serif", fontWeight: 700, fontSize: "1.7rem",
                  color: D.text, lineHeight: 1.2, textShadow: "0 0 30px rgba(0,210,200,.2)",
                }}>
                  Notices
                </Typography>
                <Typography sx={{ fontFamily: "'DM Sans',sans-serif", fontSize: "0.82rem", color: D.textSub }}>
                  Manage and track notices for all employees
                  {isAdmin && (
                    <span style={{ marginLeft: 8, color: D.teal, fontWeight: 600 }}>(Admin Mode - Full Access)</span>
                  )}
                  {!isAdmin && (
                    <span style={{ marginLeft: 8, color: "#f57c00", fontWeight: 600 }}>(View Only)</span>
                  )}
                </Typography>
              </Box>
            </Box>

            {/* ── Filter bar ── */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap",
              background: D.filterBg,
              borderRadius: "14px",
              border: "1px solid rgba(0,210,200,.18)",
              p: 1.5,
              boxShadow: "0 4px 24px rgba(0,0,0,.5), inset 0 1px 0 rgba(0,210,200,.07)",
              backdropFilter: "blur(12px)",
            }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search notices..."
                value={search}
                onChange={this.handleSearchChange}
                sx={{
                  flex: 1, minWidth: 200,
                  "& .MuiOutlinedInput-root": {
                    background: "rgba(0,210,200,.06)", borderRadius: "10px", height: 40, fontSize: "0.82rem",
                    "& input": { color: D.text, fontFamily: "'DM Sans',sans-serif" },
                    "& input::placeholder": { color: D.textSub, opacity: 1 },
                    "& fieldset": { borderColor: "rgba(0,210,200,.2)" },
                    "&:hover fieldset": { borderColor: "rgba(0,210,200,.45)" },
                    "&.Mui-focused fieldset": { borderColor: D.teal },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: "1rem", color: D.textSub }} />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel sx={labelSx}>Year</InputLabel>
                <Select
                  value={yearFilter}
                  label="Year"
                  onChange={this.handleYearChange}
                  sx={selectSx}
                  MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                  disabled={loading}
                >
                  {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel sx={labelSx}>Category</InputLabel>
                <Select
                  value={catFilter}
                  label="Category"
                  onChange={this.handleCategoryChange}
                  sx={selectSx}
                  MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                  disabled={loading}
                >
                  {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 110 }}>
                <InputLabel sx={labelSx}>Priority</InputLabel>
                <Select
                  value={priFilter}
                  label="Priority"
                  onChange={this.handlePriorityChange}
                  sx={selectSx}
                  MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                  disabled={loading}
                >
                  {priorities.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                </Select>
              </FormControl>

              <Button
                onClick={this.clearFilters}
                startIcon={<RefreshIcon sx={{ fontSize: "0.9rem !important" }} />}
                sx={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", fontWeight: 500,
                  textTransform: "none", borderRadius: "8px", px: 1.8, py: 0.75,
                  color: D.teal, border: "1px solid rgba(0,210,200,.25)",
                  background: "rgba(0,210,200,.06)", whiteSpace: "nowrap",
                  "&:hover": { background: "rgba(0,210,200,.14)", boxShadow: "0 0 10px rgba(0,210,200,.1)" },
                }}
                disabled={loading}
              >
                Clear Filters
              </Button>

              <Button
                onClick={this.handleRefresh}
                startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon sx={{ fontSize: "0.9rem !important" }} />}
                sx={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", fontWeight: 500,
                  textTransform: "none", borderRadius: "8px", px: 1.8, py: 0.75,
                  color: D.teal, border: "1px solid rgba(0,210,200,.25)",
                  background: "rgba(0,210,200,.06)", whiteSpace: "nowrap",
                  "&:hover": { background: "rgba(0,210,200,.14)" },
                }}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>

              <Button
                startIcon={<FileDownloadIcon sx={{ fontSize: "0.9rem !important" }} />}
                sx={{
                  fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", fontWeight: 500,
                  textTransform: "none", borderRadius: "8px", px: 1.8, py: 0.75,
                  color: D.teal, border: "1px solid rgba(0,210,200,.25)",
                  background: "rgba(0,210,200,.06)", whiteSpace: "nowrap",
                  "&:hover": { background: "rgba(0,210,200,.14)" },
                }}
              >
                Export Data
              </Button>

              {/* Create Notice button - only visible to admin */}
              {isAdmin && (
                <Button
                  onClick={this.handleOpenCreateDialog}
                  startIcon={<AddIcon sx={{ fontSize: "0.9rem !important" }} />}
                  sx={{
                    fontFamily: "'DM Sans',sans-serif", fontSize: "0.78rem", fontWeight: 700,
                    textTransform: "none", borderRadius: "8px", px: 2, py: 0.75,
                    background: `linear-gradient(90deg, ${D.tealDark}, ${D.teal})`,
                    color: "#ffffff", border: "none", whiteSpace: "nowrap",
                    boxShadow: "0 4px 16px rgba(0,210,200,.3)",
                    "&:hover": { boxShadow: "0 6px 20px rgba(0,210,200,.45)", transform: "translateY(-1px)" },
                    transition: "all .25s ease",
                  }}
                >
                  + Create Notice
                </Button>
              )}
            </Box>

            {/* Error message */}
            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: "8px" }}>
                {error}
              </Alert>
            )}

            {/* Loading indicator */}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress sx={{ color: D.teal }} />
              </Box>
            )}

            {/* ── Grid ── */}
            {!loading && (!notices || notices.length === 0) ? (
              <Box sx={{
                textAlign: "center", py: 8,
                background: "linear-gradient(160deg, rgba(12,28,45,.96) 0%, rgba(7,18,32,.98) 100%)",
                borderRadius: "14px", border: "1px solid rgba(0,210,200,.18)",
                boxShadow: "0 4px 24px rgba(0,0,0,.5)",
              }}>
                <NotificationsActiveIcon sx={{ fontSize: 48, color: D.textSub, mb: 2, opacity: 0.3, filter: "drop-shadow(0 0 8px rgba(0,210,200,.3))" }} />
                <Typography sx={{ fontFamily: "'DM Sans',sans-serif", color: D.textSub, fontSize: "0.95rem" }}>
                  No notices available. Try refreshing or click the refresh button.
                </Typography>
              </Box>
            ) : !loading && filtered.length === 0 ? (
              <Box sx={{
                textAlign: "center", py: 8,
                background: "linear-gradient(160deg, rgba(12,28,45,.96) 0%, rgba(7,18,32,.98) 100%)",
                borderRadius: "14px", border: "1px solid rgba(0,210,200,.18)",
                boxShadow: "0 4px 24px rgba(0,0,0,.5)",
              }}>
                <NotificationsActiveIcon sx={{ fontSize: 48, color: D.textSub, mb: 2, opacity: 0.3, filter: "drop-shadow(0 0 8px rgba(0,210,200,.3))" }} />
                <Typography sx={{ fontFamily: "'DM Sans',sans-serif", color: D.textSub, fontSize: "0.95rem" }}>
                  No notices match your filters.
                </Typography>
                <Button 
                  onClick={this.clearFilters}
                  sx={{ mt: 2, color: D.teal }}
                >
                  Clear Filters
                </Button>
              </Box>
            ) : (
              !loading && (
                <Box sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr" },
                  gap: 2.5,
                }}>
                  {filtered.map(n => this.renderCard(n))}
                </Box>
              )
            )}
          </Box>
        </Box>

        {/* Create/Edit Notice Dialog */}
        {this.renderCreateDialog()}

        {/* Delete Confirmation Dialog */}
        {this.renderDeleteDialog()}

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={this.handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={this.handleCloseSnackbar} 
            severity={snackbarSeverity}
            sx={{ 
              width: '100%',
              borderRadius: '8px',
              fontFamily: "'DM Sans',sans-serif",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </>
    );
  }
}

// Map Redux state to props
const mapStateToProps = (state: any) => {
  
  return {
    notices: state.notice?.notices || [],
    filters: state.notice?.filters || { includeInactive: false },
    loading: state.notice?.loading || false,
    error: state.notice?.error || null,
    lastResponse: state.notice?.lastResponse || null,
    createSuccess: state.notice?.createSuccess || false,
    createError: state.notice?.createError || null,
    successMessage: state.notice?.successMessage || null,
  };
};

// Map Redux actions to props
const mapDispatchToProps = {
  GetAllNotices,
  SetNoticeFilters,
  ClearNoticeFilters,
  CreateNotice,
};

// Connect component to Redux
export default connect(mapStateToProps, mapDispatchToProps)(Notice);